import React, { SetStateAction, useContext } from 'react';
import { Popover } from 'src/antd';
import { T, useLocale } from '@transifex/react';
import dayjs, { Dayjs } from 'dayjs';
import { Typography } from 'src/ui/Typography';
import styled from 'styled-components';
import { apiHelperCollections } from 'src/api/helpers/collection';
import { cn } from '@/lib/utils';
import { PropertyContext } from '../../../../../../context/property-context';
import CalendarCell from './CalendarCell';
import useViewTicketsModal from './useViewTicketsModal';

type CollectionCalendarProps = {
  value: Dayjs;
  setValue: React.Dispatch<SetStateAction<Dayjs>>;
  data: $TSFixMe;
  onChange: (range: [Dayjs, Dayjs]) => void;
};

const StyledPopoverContainer = styled.div`
  & .ant-popover-inner-content {
    padding: 0px !important;
  }
`;

const buildMonthWeeks = (anchor: Dayjs): Dayjs[][] => {
  const startOfGrid = anchor.startOf('month').startOf('week');
  const endOfGrid = anchor.endOf('month').endOf('week');
  const days: Dayjs[] = [];
  let cursor = startOfGrid;
  while (cursor.isBefore(endOfGrid) || cursor.isSame(endOfGrid, 'day')) {
    days.push(cursor);
    cursor = cursor.add(1, 'day');
  }
  const weeks: Dayjs[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
};

export const CollectionCalendar = ({
  value,
  data,
}: CollectionCalendarProps) => {
  const { location } = useContext(PropertyContext);

  // Re-render weekday/month labels when Transifex flips the locale.
  useLocale();

  const {
    jsx: { modal: jsxModal },
    utils: { openModal, setState: setModalState },
  } = useViewTicketsModal();

  const anchor = value || dayjs();
  const weeks = buildMonthWeeks(anchor);

  const firstDayOfWeek = dayjs.localeData().firstDayOfWeek();
  const weekdayLabels = dayjs.weekdaysMin();
  const orderedLabels = [
    ...weekdayLabels.slice(firstDayOfWeek),
    ...weekdayLabels.slice(0, firstDayOfWeek),
  ];

  const onCalendarItemClick = (item: $TSFixMe) => {
    const status = apiHelperCollections.getStatus(item);
    const hasTickets = apiHelperCollections.hasTickets(item);
    if (hasTickets) {
      setModalState({
        tickets: item?.pickup_order?.created_tickets,
        collectionStatus: status,
      });
      openModal();
    }
  };

  const renderCell = (date: Dayjs) => {
    const isOtherMonth = date.month() !== anchor.month();
    const isToday = dayjs().isSame(date, 'day');
    const dayKey = date.format('YYYY-MM-DD');
    const listData = data[dayKey] || [];
    const collectionsToShowOnCell = listData.slice(0, 3);
    const collectionsToShowOnPopup = listData.slice(3) ?? [];

    return (
      <div
        key={dayKey}
        role="gridcell"
        aria-current={isToday ? 'date' : undefined}
        className={cn(
          'flex flex-1 min-w-px min-h-[160px] flex-col',
          isOtherMonth && 'opacity-50'
        )}
      >
        <div
          className={cn(
            'flex flex-1 items-start justify-between gap-2 p-4 border-t border-solid',
            isToday
              ? 'bg-[var(--bg-active)] border-[var(--border-focus)]'
              : 'border-border'
          )}
        >
          <div className="flex flex-1 min-w-0 flex-col items-start gap-2">
            {collectionsToShowOnCell.map((item: $TSFixMe) => (
              <CalendarCell
                key={item?.id}
                onClick={() => onCalendarItemClick(item)}
                item={item}
              />
            ))}
            {collectionsToShowOnPopup.length !== 0 && (
              <StyledPopoverContainer>
                <Popover
                  showArrow={false}
                  overlayClassName="whf-ant-popover no-content-padding no-arrow"
                  content={
                    <div className="flex max-h-[250px] w-[215px] flex-col gap-2 overflow-auto p-2">
                      {collectionsToShowOnPopup.map((item: $TSFixMe) => (
                        <CalendarCell
                          key={item?.id}
                          onClick={() => onCalendarItemClick(item)}
                          item={item}
                        />
                      ))}
                    </div>
                  }
                >
                  <Typography style={{ color: '#1890FF' }}>
                    <T
                      _str="{count} more"
                      count={`+${collectionsToShowOnPopup.length}`}
                    />
                  </Typography>
                </Popover>
              </StyledPopoverContainer>
            )}
          </div>
          <span
            className={cn(
              'shrink-0 text-base leading-[1.5] text-right whitespace-nowrap text-[var(--text-primary)]',
              isToday ? 'font-semibold' : 'font-normal'
            )}
          >
            {date.date()}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-1 flex-col">
      <div className="print-info hidden print:flex print:flex-row print:justify-between">
        <div>
          <T _str="Address: {location}" location={location?.name} />
        </div>
        <div>
          <T _str="Printed on: {date}" date={dayjs().format('LL')} />
        </div>
      </div>
      <div className="flex w-full items-start pb-4" role="row">
        {orderedLabels.map((label) => (
          <div
            key={label}
            className="flex flex-1 min-w-px flex-col items-end px-4"
            role="columnheader"
          >
            <span className="text-base font-normal leading-[1.5] text-foreground text-right whitespace-nowrap">
              {label}
            </span>
          </div>
        ))}
      </div>
      <div className="flex w-full flex-1 flex-col" role="rowgroup">
        {weeks.map((week) => (
          <div
            key={week[0].format('YYYY-MM-DD')}
            role="row"
            className="flex w-full flex-1 items-stretch"
          >
            {week.map((d) => renderCell(d))}
          </div>
        ))}
      </div>
      {jsxModal}
    </div>
  );
};
